import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import { userService } from '../../services/userService';
import {
  getTotpNudgeCopy,
  getTotpNudgeTarget,
  resolveTotpNudgeDecision,
} from '../../services/totpNudge';
import { Button } from './UIComponents';

interface TotpNudgeCardProps {
  placement: 'settings' | 'shell';
  className?: string;
}

const TotpNudgeCard: React.FC<TotpNudgeCardProps> = ({ placement, className = '' }) => {
  const navigate = useNavigate();
  const { currentUser, userProfile, claims } = useAuth();
  const [hidden, setHidden] = useState(false);
  const [snoozeLoading, setSnoozeLoading] = useState(false);
  const [snoozeError, setSnoozeError] = useState<string | null>(null);
  const readiness = authService.getTotpMfaReadiness(currentUser);
  const isSettingsPlacement = placement === 'settings';

  const decision = useMemo(
    () =>
      resolveTotpNudgeDecision(userProfile, claims, readiness, {
        ignoreSnooze: isSettingsPlacement,
      }),
    [userProfile, claims, readiness, isSettingsPlacement]
  );

  if (!currentUser?.uid || hidden || !decision.shouldShow) {
    return null;
  }

  const copy = getTotpNudgeCopy(decision.audience);
  const target = getTotpNudgeTarget(decision.audience);
  const titleId = `totp-nudge-${placement}-title`;

  const handleEnable = () => {
    navigate(target);
  };

  const handleSnooze = async () => {
    setSnoozeLoading(true);
    setSnoozeError(null);
    try {
      await userService.snoozeTotpSecurityNudge(currentUser.uid);
      setHidden(true);
    } catch (error) {
      console.error('[TotpNudge] snooze failed', error);
      setSnoozeError('Nie udało się zapisać odroczenia. Spróbuj ponownie.');
    } finally {
      setSnoozeLoading(false);
    }
  };

  return (
    <section
      aria-labelledby={titleId}
      className={`rounded-lg border border-ratuj-300/20 bg-primary-dark-100/42 p-3.5 text-primary-light-100 sm:p-4 ${className}`}
      data-testid={`totp-nudge-${placement}`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ShieldCheckIcon className="h-5 w-5 flex-none text-ratuj-300" aria-hidden="true" />
            <h3 id={titleId} className="text-sm font-semibold text-primary-light-100">
              {copy.title}
            </h3>
          </div>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-primary-light-300">
            {copy.description}
          </p>
          <p className="mt-2 text-xs leading-5 text-primary-light-400">{copy.helper}</p>
          {snoozeError && (
            <p className="mt-2 text-xs leading-5 text-status-error-400">{snoozeError}</p>
          )}
        </div>
        <div className="flex shrink-0 flex-col gap-2 sm:min-w-[148px]">
          <Button type="button" size="sm" variant="primary" onClick={handleEnable} fullWidth>
            Włącz 2FA
          </Button>
          <Button
            type="button"
            size="sm"
            variant="neutral"
            onClick={handleSnooze}
            isLoading={snoozeLoading}
            disabled={snoozeLoading}
            fullWidth
          >
            Później
          </Button>
        </div>
      </div>
    </section>
  );
};

export default TotpNudgeCard;
